import { useState, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Pin,
  Clock,
  Sparkles,
  Calendar as CalendarIcon,
} from 'lucide-react';
import type { ComunicadoItem, ComunicadosConfig } from './ComunicadosConfig';

interface Props {
  comunicados: ComunicadoItem[];
  config: ComunicadosConfig;
  onSelectComunicado: (c: ComunicadoItem) => void;
  onCreateOnDate?: (dateStr: string) => void;
}

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

const CAT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  OPERACIONAL: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  INSTITUCIONAL: { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
  PEDAGOGICO: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
  TEMPLATE: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
};

export function ComunicadosCalendarView({
  comunicados,
  config,
  onSelectComunicado,
  onCreateOnDate,
}: Props) {
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthName = currentDate.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const setToday = () => {
    setCurrentDate(new Date());
  };

  // Mapeamento de comunicados por data (YYYY-MM-DD)
  const itemsByDate = useMemo(() => {
    const map: Record<string, ComunicadoItem[]> = {};

    comunicados.forEach((c) => {
      // Prioriza dataEvento, depois agendadoPara, depois criadoEm
      const rawDate = c.dataEvento || c.agendadoPara || c.criadoEm;
      if (!rawDate) return;

      try {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (!map[key]) map[key] = [];
          map[key].push(c);
        }
      } catch {
        // Ignora data inválida
      }
    });

    return map;
  }, [comunicados]);

  // Construção dos dias da grade (42 células = 6 semanas)
  const calendarCells = useMemo(() => {
    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const cells: Array<{
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      items: ComunicadoItem[];
    }> = [];

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Dias do mês anterior
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const m = month === 0 ? 12 : month;
      const y = month === 0 ? year - 1 : year;
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        items: itemsByDate[dateStr] || [],
      });
    }

    // Dias do mês atual
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        items: itemsByDate[dateStr] || [],
      });
    }

    // Dias do próximo mês para completar 35 ou 42 células
    const remaining = (cells.length <= 35 ? 35 : 42) - cells.length;
    for (let day = 1; day <= remaining; day++) {
      const m = month === 11 ? 1 : month + 2;
      const y = month === 11 ? year + 1 : year;
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        items: itemsByDate[dateStr] || [],
      });
    }

    return cells;
  }, [year, month, itemsByDate]);

  // Contagem de eventos no mês atual
  const totalEventsInMonth = useMemo(() => {
    return calendarCells
      .filter((c) => c.isCurrentMonth)
      .reduce((acc, c) => acc + c.items.length, 0);
  }, [calendarCells]);

  return (
    <div className="cmc-calendar-wrap">
      {/* Header de Navegação do Calendário */}
      <div className="cmc-cal-header">
        <div className="cmc-cal-title-area">
          <h2 className="cmc-cal-month-title">
            <span style={{ textTransform: 'capitalize' }}>{monthName}</span>
          </h2>
          <span className="cmc-cal-badge-count">
            {totalEventsInMonth} {totalEventsInMonth === 1 ? 'comunicado' : 'comunicados'} neste mês
          </span>
        </div>

        <div className="cmc-cal-nav-buttons">
          <button
            type="button"
            className="cmc-cal-btn-today"
            onClick={setToday}
          >
            Hoje
          </button>
          <div className="cmc-cal-arrow-group">
            <button
              type="button"
              className="cmc-cal-arrow-btn"
              onClick={prevMonth}
              title="Mês anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="cmc-cal-arrow-btn"
              onClick={nextMonth}
              title="Próximo mês"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Grade de Dias da Semana */}
      <div className="cmc-cal-weekdays">
        {WEEKDAYS.map((wd, i) => (
          <div
            key={wd}
            className={`cmc-cal-weekday ${i === 0 || i === 6 ? 'weekend' : ''}`}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Grade de 35 ou 42 Dias */}
      <div className="cmc-cal-grid">
        {calendarCells.map((cell) => {
          return (
            <div
              key={cell.dateStr}
              className={`cmc-cal-day-cell ${cell.isCurrentMonth ? 'in-month' : 'out-month'} ${cell.isToday ? 'is-today' : ''}`}
              onClick={() => {
                if (onCreateOnDate && config.features?.canManage !== false) {
                  onCreateOnDate(cell.dateStr);
                }
              }}
            >
              <div className="cmc-cal-day-header">
                <span className={`cmc-cal-day-num ${cell.isToday ? 'today-pill' : ''}`}>
                  {cell.dayNumber}
                </span>

                {config.features?.canManage !== false && cell.isCurrentMonth && (
                  <button
                    type="button"
                    className="cmc-cal-add-day-btn"
                    title={`Agendar comunicado para ${cell.dayNumber}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateOnDate?.(cell.dateStr);
                    }}
                  >
                    <Plus size={11} />
                  </button>
                )}
              </div>

              {/* Lista de Pills de Comunicados */}
              <div className="cmc-cal-items-list">
                {cell.items.slice(0, 3).map((c) => {
                  const colors = CAT_COLORS[c.categoria] || CAT_COLORS.OPERACIONAL;
                  const isScheduled = c.status === 'AGENDADO';

                  return (
                    <div
                      key={c.id}
                      className="cmc-cal-pill"
                      style={{
                        background: isScheduled ? '#fffbeb' : colors.bg,
                        color: isScheduled ? '#b45309' : colors.text,
                        borderColor: isScheduled ? '#fde68a' : colors.border,
                      }}
                      title={`${c.titulo} (${c.categoria}) - Clique para ver detalhes`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectComunicado(c);
                      }}
                    >
                      {c.fixado && <Pin size={10} style={{ flexShrink: 0 }} />}
                      {isScheduled && <Clock size={10} style={{ flexShrink: 0 }} />}
                      <span className="cmc-cal-pill-text">{c.titulo}</span>
                    </div>
                  );
                })}

                {cell.items.length > 3 && (
                  <div className="cmc-cal-more-items">
                    +{cell.items.length - 3} mais
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legenda de Categorias no Rodapé */}
      <div className="cmc-cal-legend">
        <span className="cmc-cal-legend-title">Legenda:</span>
        <div className="cmc-cal-legend-item">
          <span className="cmc-cal-dot" style={{ background: '#2563eb' }} />
          <span>Operacional</span>
        </div>
        <div className="cmc-cal-legend-item">
          <span className="cmc-cal-dot" style={{ background: '#7c3aed' }} />
          <span>Institucional</span>
        </div>
        <div className="cmc-cal-legend-item">
          <span className="cmc-cal-dot" style={{ background: '#059669' }} />
          <span>Pedagógico</span>
        </div>
        <div className="cmc-cal-legend-item">
          <span className="cmc-cal-dot" style={{ background: '#d97706' }} />
          <span>Agendado</span>
        </div>
      </div>
    </div>
  );
}
